import React, { useState, useContext } from 'react';
import '../styles/Services.css';
import { motion } from 'framer-motion';
import { FaProjectDiagram, FaLaptopCode, FaCogs, FaCode, FaCheckCircle, FaMobileAlt, FaTools, FaPencilRuler } from 'react-icons/fa';
import { LanguageContext } from '../contexts/LanguageContext';
import ContactBloc from '../components/ContactBloc';
import devLaptop from '../img/assets/dev-laptop.png';

const Services: React.FC = () => {
    const { language } = useContext(LanguageContext)!;
    const [activeService, setActiveService] = useState<number | null>(null);

    const services = {
        fr: [
            { title: 'Conception de site internet et applications', icon: <FaLaptopCode />, description: "Je conçois des sites web modernes et des applications qui répondent à vos besoins fonctionnels et esthétiques, tout en assurant une expérience utilisateur optimale." },
            { title: 'Mise en place et gestion de CMS', icon: <FaCogs />, description: "Je configure et administre des systèmes de gestion de contenu (CMS) pour vous permettre de gérer facilement votre contenu en ligne." },
            { title: 'Intégration web', icon: <FaCode />, description: "Je prends en charge l'intégration de designs web pour rendre votre site fonctionnel et accessible, tout en respectant les normes du web." },
            { title: 'Conception graphique & Webdesign', icon: <FaPencilRuler />, description: "Je conçois vos logos, templates web, cartes de visite et newsletters avec un design unique, moderne et professionnel." },
            { title: 'Gestion et suivi de projets web', icon: <FaProjectDiagram />, description: "Je m'occupe de la gestion complète de vos projets web, du concept initial au suivi post-lancement pour assurer le succès et la pérennité de vos projets." },
            { title: 'Conception multi-plateformes', icon: <FaMobileAlt />, description: "Je crée des sites web qui fonctionnent sur toutes les plateformes, qu'il s'agisse de smartphones, tablettes ou ordinateurs de bureau." },
            { title: 'Validation W3C, accessibilité & SEO', icon: <FaCheckCircle />, description: "Je m'assure que votre site respecte les standards W3C, est accessible à tous les utilisateurs et optimisé pour le référencement naturel." },
            { title: 'Développements spécifiques', icon: <FaTools />, description: "Je crée des outils sur-mesure adaptés à votre cœur de métier, et des solutions personnalisées pour répondre précisément à vos besoins." }
        ],
        en: [
            { title: 'Website design and applications', icon: <FaLaptopCode />, description: "I design modern websites and applications that meet your functional and aesthetic needs, while providing an optimal user experience." },
            { title: 'Implementation and management of CMS', icon: <FaCogs />, description: "I set up and manage content management systems (CMS), allowing you to easily manage your online content." },
            { title: 'Web integration', icon: <FaCode />, description: "I handle web integration to make your website functional and accessible, adhering to web standards and best practices." },
            { title: 'Graphic Design & Webdesign', icon: <FaPencilRuler />, description: "I design your logos, web templates, business cards, and newsletters with a unique, modern, and professional touch." },
            { title: 'Web project management and monitoring', icon: <FaProjectDiagram />, description: "I manage the entire lifecycle of your web projects, from initial concept to post-launch monitoring, ensuring success and long-term sustainability." },
            { title: 'Multi-platform Design', icon: <FaMobileAlt />, description: "I design websites that work seamlessly across all platforms, whether it be smartphones, tablets, or desktop computers." },
            { title: 'W3C validation, accessibility & SEO', icon: <FaCheckCircle />, description: "I ensure that your website meets W3C standards, is accessible to all users, and optimized for search engine ranking." },
            { title: 'Custom developments', icon: <FaTools />, description: "I create custom tools tailored to your business, offering personalized solutions that meet your specific needs." }
        ]
    };

    const handleServiceClick = (index: number) => {
        setActiveService(activeService === index ? null : index);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="services"
        >
            <motion.div className="services-header" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <p>{language === 'fr' ? 'Services' : 'Services'}</p>
                <h1>{language === 'fr' ? "Des services qui s'adaptent" : 'Services that adapt'}</h1>
            </motion.div>

            <div className="services-content">
                <motion.div
                    className="services-column"
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.5 }}
                >
                    {services[language].slice(0, 4).map((service, index) => (
                        <motion.div
                            className={`service-item ${activeService === index ? 'active' : ''}`}
                            key={index}
                            onClick={() => handleServiceClick(index)}
                            whileHover={{ scale: 1.05 }}
                        >
                            <div className="service-header">
                                <div className="icon">{service.icon}</div>
                                <h2>{service.title}</h2>
                            </div>
                            <motion.p
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: activeService === index ? 'auto' : 0, opacity: activeService === index ? 1 : 0 }}
                                transition={{ duration: 0.5, ease: 'easeInOut' }}
                            >
                                {service.description}
                            </motion.p>
                        </motion.div>
                    ))}
                </motion.div>

                <div className="services-image-container">
                    <motion.img
                        src={devLaptop}
                        alt="Laptop illustration"
                        className="services-image"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    />
                </div>

                <motion.div
                    className="services-column"
                    initial={{ x: 100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.9 }}
                >
                    {services[language].slice(4).map((service, index) => (
                        <motion.div
                            className={`service-item ${activeService === index + 4 ? 'active' : ''}`}
                            key={index + 4}
                            onClick={() => handleServiceClick(index + 4)}
                            whileHover={{ scale: 1.05 }}
                        >
                            <div className="service-header">
                                <div className="icon">{service.icon}</div>
                                <h2>{service.title}</h2>
                            </div>
                            <motion.p
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: activeService === index + 4 ? 'auto' : 0, opacity: activeService === index + 4 ? 1 : 0 }}
                                transition={{ duration: 0.5, ease: 'easeInOut' }}
                            >
                                {service.description}
                            </motion.p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>

            <ContactBloc />
        </motion.div>
    );
};

export default Services;
